// What the hero rabbit does, as a timeline of segments generated from a seed:
// it peeks out of its burrow, hops around in runs of 3-7 hops, stops to
// nibble / look around / stand up / wash its face, hops off-screen, comes
// back mirrored, and sometimes dives back into the burrow and peeks out again
// later.
//
// The timeline is generated ahead of time and every frame is a pure function
// of (timeline, time), so the component can render any moment on demand
// (window.__hero.rabbit.renderAt) and pausing never makes anything jump.
// Positions are stored relative to the hero width ({ u, s }: u * width +
// s * rabbit size), so a stop stays on the same spot of the meadow when the
// width changes mid-run. Hop timings follow from hop lengths, though, so a
// width change also re-plans everything after the current action (replan).

import { seededRandom, lerp, ease } from './heroMotion';
import { POSES, HIND_KEYS, blendInto, blendKeys, copyPose, newPose, noseOf, earTopOf } from './HeroRabbitRig';

const ENTRANCE_MS = 2300;
// Until the burrow has actually been seen the component holds its clock
// here, so the peek-out is never played below the fold.
export const ENTRANCE_HOLD = ENTRANCE_MS - 400;

const DP = 5 - POSES.launch.hNx; // body travel while the hind legs push off (rig units)
const DL = 26; // body travel over the front paws after touch-down
const TP_MAX = 300; // longest push-off (ms)
// Upright in the burrow the body sits behind the rig origin; this centres it
// on the mouth of the hole.
const PEEK_SHIFT = 11;
const HOLE_LINE = -1.5; // rig y of the hole's rim line (see HeroRabbit.vue measure)
const TURN_MS = 540; // ducking into the hole to turn round
const TAU = Math.PI * 2;

/* ---------------------------------------------------------------------- */
/* Small helpers                                                          */
/* ---------------------------------------------------------------------- */

function sat(v) {
  return v < 0 ? 0 : (v > 1 ? 1 : v);
}

function span(t, a, b) {
  return sat((t - a) / (b - a));
}

// Where the burrow sits, as a share of the hero width: a little nearer the
// middle on wide screens so the peek-out is not lost in a corner.
export function burrowU(W) {
  return 0.22 + 0.07 * sat((W - 1000) / 900);
}

export function burrowX(env) {
  return burrowU(env.W) * env.W;
}

// Cubic Hermite from p0 to p1 with end tangents m0, m1 (per unit of s): lets a
// push-off accelerate into exactly the flight speed and a landing brake from it.
function hermite(s, p0, p1, m0, m1) {
  const s2 = s * s;
  const s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * p0 + (s3 - 2 * s2 + s) * m0 + (-2 * s3 + 3 * s2) * p1 + (s3 - s2) * m1;
}

// 0 -> 1 -> 0 envelope with eased ramps.
function ramp(u, dur, tin, tout) {
  if (u <= 0 || u >= dur) return 0;
  if (u < tin) return ease.inOutSine(u / tin);
  if (u > dur - tout) return ease.inOutSine((dur - u) / tout);
  return 1;
}

// Pose blends hand over with matching speeds: the push accelerates into the
// take-off, the flight starts fast and settles, the reach accelerates into
// the touch-down.
function easeIn(s) {
  return s * s;
}

function easeOut(s) {
  return 1 - (1 - s) * (1 - s);
}

// A Hermite end tangent kept within `ratio` times the distance covered (and
// of the same sign), so the curve never overshoots or backs up first.
function clampTangent(m, dist, ratio) {
  const lim = ratio * dist;
  return lim < 0 ? Math.min(Math.max(m, lim), 0) : Math.max(Math.min(m, lim), 0);
}

function bump(t, a, b) {
  return Math.sin(Math.PI * span(t, a, b));
}

// A quick flick that settles with a small overshoot (ears, tail).
function flick(s) {
  if (s <= 0 || s >= 1) return 0;
  return Math.sin(2.5 * Math.PI * s) * Math.exp(-3.5 * s) * (1 - s) * 2.2;
}

function hash01(i, seed) {
  let x = Math.imul((i | 0) ^ seed, 0x9E3779B1);
  x ^= x >>> 15;
  x = Math.imul(x, 0x85EBCA77);
  x ^= x >>> 13;
  x = Math.imul(x, 0xC2B2AE3D);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

function pulse(dt, dur) {
  if (dt <= 0 || dt >= dur) return 0;
  return Math.sin(Math.PI * dt / dur);
}

export function posX(p, env) {
  return p.u * env.W + p.s * env.S;
}

function toPos(x, env) {
  if (x < 0) return { u: 0, s: x / env.S };
  if (x > env.W) return { u: 1, s: (x - env.W) / env.S };
  return { u: x / env.W, s: 0 };
}

// Blinks happen on their own clock, rarely (a wild rabbit barely blinks).
export function blinkAt(t, seed) {
  const period = 11000;
  const i = Math.floor(t / period);
  let v = 0;
  for (let j = i - 1; j <= i; j++) {
    const at = j * period + 400 + hash01(j, seed) * 8000;
    v = Math.max(v, pulse(t - at, 170));
  }
  return v;
}

// The nose never really stops: fast twitches in bursts.
export function noseAt(t) {
  const s = t / 1000;
  const env = 0.5 + 0.5 * Math.sin(s * TAU / 2.3 + 1.3) * Math.sin(s * TAU / 3.7);
  return Math.sin(s * TAU * 7) * (0.25 + 0.75 * env * env);
}

/* ---------------------------------------------------------------------- */
/* Geometry the timeline needs                                            */
/* ---------------------------------------------------------------------- */

const PEEK_DIP = copyPose(newPose(), POSES.peek);
PEEK_DIP.eN -= 12;
PEEK_DIP.eF -= 8;
PEEK_DIP.hr += 6;
PEEK_DIP.cy += 3;

// How deep the upright rabbit must sink for its ear tips to be under the rim
// (ears pricked straight up, as when it ducks).
const PEEK_UP = copyPose(newPose(), POSES.peek);
PEEK_UP.eN = 8;
PEEK_UP.eF = 2;
const HIDE_DEPTH = -earTopOf(PEEK_UP) + 6;

// The dive: the body slides down the tunnel along its own axis, so the part
// of it crossing the rim stays put, centred on the hole. The nose sits a
// little above that axis, so it enters the mouth DIVE_ENTRY ahead of the
// centre.
const NOSE_DIVE = (function () {
  const n = noseOf(POSES.dive, [0, 0]);
  return [n[0] - POSES.dive.cx, n[1] - POSES.dive.cy];
}());
const SLIDE = [Math.cos(POSES.dive.cr * Math.PI / 180), Math.sin(POSES.dive.cr * Math.PI / 180)];
const DIVE_ENTRY = (NOSE_DIVE[0] * SLIDE[1] - NOSE_DIVE[1] * SLIDE[0]) / SLIDE[1];
const DIVE_DEPTH = 150; // slide length until even the toes are in

/* ---------------------------------------------------------------------- */
/* Timeline generation                                                    */
/* ---------------------------------------------------------------------- */

export function createPlanner(seed) {
  return {
    seed: seed | 0,
    rng: seededRandom(seed),
    segs: [],
    end: 0,
    idx: 0,
    next: { kind: 'burrow', dir: 1, wait: ENTRANCE_MS, first: true }
  };
}

// The push lasts long enough for the body to reach the flight speed with a
// steady acceleration (average push speed ~0.62 of it), and the landing
// brakes from it the same way after touch-down.
function hopTiming(D, ta, pushCx, launchCx, dp) {
  const xL = dp + launchCx;
  const xR = Math.max(D + POSES.gather.cx - DL, xL + 10);
  const v = (xR - xL) / ta;
  return {
    tp: Math.min(Math.max(1.6 * (xL - pushCx) / v, 130), TP_MAX),
    tl: Math.min(Math.max(DL / (0.55 * v), 100), 180)
  };
}

// Push travel (rig units) for a flight that comes down at xR: the full leg
// extension when there is room, less for a short hop, so that the push can
// always accelerate steadily into the flight speed within TP_MAX.
function pushFor(xR, ta, pushCx) {
  const lc = POSES.launch.cx;
  const c0 = lc - pushCx;
  const most = (TP_MAX * (xR - lc) - 1.5 * ta * c0) / (TP_MAX + 1.5 * ta);
  return Math.min(DP, Math.max(14, most));
}

function makeHop(rng, x0, x1, dir, from, env) {
  const bl = Math.abs(posX(x1, env) - posX(x0, env)) / env.S;
  const ta = Math.min(Math.max(312 + (bl - 1.9) * 100 + (rng() - 0.5) * 40, 280), 430);
  // Height from the flight time (g ~ 2300 units/s^2 for a 100-unit rabbit).
  const h = Math.min(Math.max(2300 * Math.pow(ta / 1000, 2) / 8, 22), 40) * (0.92 + rng() * 0.16);
  const tc = from === 'sit' ? 200 + rng() * 70 : 0;
  const pushCx = from === 'sit' ? POSES.crouch.cx : POSES.gather.cx;
  const dp = pushFor(bl * 100 + POSES.gather.cx - DL, ta, pushCx);
  const tm = hopTiming(bl * 100, ta, pushCx, POSES.launch.cx, dp);
  const tg = 95 + rng() * 120;
  // bl (the length in body sizes) lets a hop under way when the width
  // changes keep the proportions its timing was made for (see replan).
  return { type: 'hop', x0, x1, bl, dir, from, tc, tp: tm.tp, ta, tl: tm.tl, tg, h, dp, dur: tc + tm.tp + ta + tm.tl + tg };
}

// A stop: two or three things in a row (nibbling, sniffing the air,
// standing up, a face wash now and then), ear twitches and breathing.
function makeIdle(rng, x, dir, opts) {
  const short = opts && opts.short;
  const events = [];
  let dur;
  if (short) {
    dur = 1300 + rng() * 600;
    events.push({ kind: 'sniff', t0: 200, dur: dur - 400 });
  } else {
    const pool = ['nibble', 'sniff', 'nibble', 'alert', 'sniff', 'nibble', 'groom', 'alert'];
    const target = 2500 + rng() * 2500;
    let t = 220 + rng() * 260;
    let last = '';
    for (let n = 0; n < 3; n++) {
      let kind = pool[Math.floor(rng() * pool.length)];
      if (kind === last) kind = kind === 'nibble' ? 'sniff' : 'nibble';
      let len = kind === 'nibble' ? 1500 + rng() * 800
        : kind === 'alert' ? 1500 + rng() * 700
          : kind === 'groom' ? 1800 + rng() * 500 : 900 + rng() * 600;
      if (n === 2 && t + len > target) break;
      if (n === 1 && t + len > 5000) {
        // Not much time left: a quick sniff of the air.
        kind = 'sniff';
        len = 650 + rng() * 250;
      }
      events.push({ kind, t0: t, dur: len });
      last = kind;
      t += len + 200 + rng() * 250;
    }
    dur = Math.max(target, t + 60);
  }
  const twitches = [];
  const nt = 1 + Math.floor(rng() * 3);
  for (let i = 0; i < nt; i++) {
    twitches.push({ t: 200 + rng() * (dur - 500), ear: rng() < 0.5 ? 0 : 1, amp: (rng() < 0.5 ? -1 : 1) * (9 + rng() * 8) });
  }
  return { type: 'idle', x, dir, dur, events, twitches, phase: rng() * TAU };
}

// The jump out of the hole lands jumpLen body sizes from the burrow; it is
// measured from the burrow at any width, so its timing always fits.
function emergeLandX(seg, env) {
  return burrowX(env) + seg.dir * seg.len * env.S;
}

function makeEmerge(rng, dir, jumpLen, first) {
  // Often it looks out the other way first, then ducks back into the hole
  // and pops up facing the way it will go.
  const turn = rng() < (first ? 0.4 : 0.6);
  const T = {};
  T.ears = 420;
  T.hold = T.ears + 160 + rng() * 100;
  T.head = T.hold + 360;
  T.turnAt = turn ? T.head + 430 + rng() * 80 : -1;
  T.look = T.head + (first ? 900 : 900 + rng() * 500) + (turn ? TURN_MS : 0);
  T.rise = T.look + 300;
  T.dip = T.rise + 160;
  // Out of the hole the body rises more than it travels: a short, steep push.
  const jump = { tc: 0, ta: 400, tg: 140, h: 30, dp: 14, y0: 53 };
  const tm = hopTiming(jumpLen * 100 - PEEK_SHIFT, jump.ta, PEEK_DIP.cx, POSES.launchOut.cx, jump.dp);
  jump.tp = tm.tp;
  jump.tl = tm.tl;
  const dur = T.dip + jump.tp + jump.ta + jump.tl + jump.tg;
  return { type: 'emerge', dir, look: turn ? -dir : dir, len: jumpLen, T, jump, dur, first: !!first };
}

// Body travel (rig units) from the take-off to the nose entering the hole,
// for a take-off `gap` body sizes before the burrow.
function diveReach(gap) {
  return gap * 100 + DIVE_ENTRY - NOSE_DIVE[0];
}

// x: where it takes off (px). The take-off is kept as a distance from the
// burrow in body sizes, so a width change mid-dive cannot stretch the leap.
function makeDive(rng, x, dir, env) {
  const gap = Math.max((burrowX(env) - x) * dir / env.S, 0.6);
  const seg = { type: 'dive', gap, dir, tc: 220 + rng() * 60, ta: 340, tsink: 560, h: 20 };
  const xH = diveReach(gap);
  seg.dp = pushFor(xH, seg.ta, POSES.crouch.cx);
  const xL = seg.dp + POSES.launch.cx;
  seg.tp = Math.min(Math.max(1.6 * (xL - POSES.crouch.cx) / (Math.max(xH - xL, 20) / seg.ta), 130), TP_MAX);
  seg.dur = seg.tc + seg.tp + seg.ta + seg.tsink + 60;
  return seg;
}

// Hop lengths covering `total` (px) with natural variation.
function splitHops(rng, total, S) {
  const m = Math.max(1, Math.round(total / (2.3 * S)));
  const lens = [];
  let acc = 0;
  for (let i = 0; i < m; i++) {
    const L = i === m - 1 ? total - acc : (total / m) * (0.9 + rng() * 0.2);
    lens.push(L);
    acc += L;
  }
  return lens;
}

// One pass: from the burrow or an edge, runs of hops with stops in between,
// ending off-screen or back in the burrow, followed by a wait.
function genPass(pl, env) {
  const rng = pl.rng;
  const st = pl.next;
  const W = env.W;
  const S = env.S;
  const Bx = burrowX(env);
  let t = pl.end;
  const add = (seg) => {
    seg.t0 = t;
    seg.t1 = t + seg.dur;
    t = seg.t1;
    pl.segs.push(seg);
  };

  const dir = st.dir;
  let x;
  let from = 'gather';
  if (st.kind === 'burrow') {
    if (st.wait) add({ type: 'hidden', dir, dur: st.wait });
    const jumpLen = 1.45 + rng() * 0.25;
    const em = makeEmerge(rng, dir, jumpLen, st.first);
    add(em);
    x = emergeLandX(em, env);
  } else if (st.kind === 'edge') {
    x = dir > 0 ? -1.4 * S : W + 1.4 * S;
  } else {
    // Carrying on from the meadow (st.x is a stored position).
    x = posX(st.x, env);
    from = st.from || 'sit';
  }

  const exitX = dir > 0 ? W + 1.4 * S : -1.4 * S;
  const approachX = Bx - dir * S * 1.7;
  const dive = st.kind === 'edge' && (approachX - x) * dir > 2.6 * S && rng() < 0.45;
  const goal = dive ? approachX : exitX;

  // Stops happen between lo and hi: on screen, clear of the edges.
  const lo = Math.max(0.06 * W, 1.1 * S);
  const hi = Math.min(0.94 * W, W - 1.1 * S);
  const edge = dir > 0 ? hi : lo;
  let stops = 0;
  for (let run = 0; run < 14; run++) {
    const remaining = (goal - x) * dir;
    if (remaining <= 0.3 * S) break;
    const n = st.first && run === 0 ? 3 : 3 + Math.floor(rng() * 5);
    let lens = [];
    let sum = 0;
    for (let i = 0; i < n; i++) {
      const L = S * (1.85 + rng() * 0.95);
      lens.push(L);
      sum += L;
    }
    const room = (edge - x) * dir;
    const reachesGoal = sum >= remaining - 0.5 * S;
    const pastEdge = sum > room;
    let last = false;
    if ((reachesGoal || pastEdge) && !dive && room > (stops === 0 ? 1.6 : 8) * S) {
      // Never cross the whole screen without a pause (narrow screens), nor
      // hop on for ages before leaving (wide ones): stop somewhere ahead.
      let xs = x + dir * room * (0.45 + rng() * 0.35);
      if (Math.abs(xs - Bx) < 1.3 * S) {
        // Not on the burrow: just before it, or just past it.
        const before = Bx - dir * 1.5 * S;
        xs = (before - x) * dir > 1.2 * S ? before : Bx + dir * 1.5 * S;
      }
      lens = splitHops(rng, (xs - x) * dir, S);
    } else if (reachesGoal || (pastEdge && !dive)) {
      last = true;
      if (dive) {
        // The last hop ends exactly in front of the burrow.
        lens = splitHops(rng, remaining, S);
      } else {
        // Hop on until off-screen.
        let acc = 0;
        let used = 0;
        while (used < lens.length && acc < remaining) acc += lens[used++];
        lens.length = used;
        while (acc < remaining) {
          const L = S * (1.85 + rng() * 0.95);
          lens.push(L);
          acc += L;
        }
      }
    }
    if (!last) {
      // A stop that would fall on the burrow moves just before or past it.
      const xEnd = x + dir * lens.reduce((acc, L) => acc + L, 0);
      if (Math.abs(xEnd - Bx) < 1.3 * S) {
        const before = Bx - dir * 1.5 * S;
        const after = Bx + dir * 1.5 * S;
        const xs = (before - x) * dir > 1.2 * S || after < lo || after > hi ? before : after;
        if ((xs - x) * dir > 1.15 * S) lens = splitHops(rng, (xs - x) * dir, S);
      }
    }
    for (let i = 0; i < lens.length; i++) {
      const x1 = x + dir * lens[i];
      add(makeHop(rng, toPos(x, env), toPos(x1, env), dir, from, env));
      from = 'gather';
      x = x1;
    }
    if (last) break;
    // Never stop on top of the burrow: one more hop.
    if (Math.abs(x - Bx) < 1.2 * S) {
      const x1 = x + dir * S * (2 + rng() * 0.4);
      add(makeHop(rng, toPos(x, env), toPos(x1, env), dir, from, env));
      x = x1;
      if (x < lo || x > hi) continue;
    }
    add({ type: 'settle', x: toPos(x, env), dir, dur: 360 });
    add(makeIdle(rng, toPos(x, env), dir));
    from = 'sit';
    stops++;
  }

  if (dive) {
    add({ type: 'settle', x: toPos(x, env), dir, dur: 360 });
    add(makeIdle(rng, toPos(x, env), dir, { short: true }));
    add(makeDive(rng, x, dir, env));
    pl.next = { kind: 'burrow', dir: rng() < 0.7 ? 1 : -1, wait: 4000 + rng() * 5000 };
  } else {
    add({ type: 'away', dir, dur: 4000 + rng() * 5000 });
    pl.next = { kind: 'edge', dir: -dir };
  }
  // The last segment of a pass remembers what follows it, so the timeline
  // can be cut there later (replan) even after more passes were generated.
  pl.segs[pl.segs.length - 1].next = pl.next;
  pl.end = t;
}

export function ensurePlan(pl, t, env) {
  let guard = 0;
  while (pl.end <= t && guard++ < 60) genPass(pl, env);
}

// Where a timeline cut right after `seg` carries on from.
function nextAfter(seg, env) {
  if (seg.next) return seg.next;
  switch (seg.type) {
    case 'hop':
      return { kind: 'meadow', dir: seg.dir, x: seg.x1, from: 'gather' };
    case 'emerge':
      return { kind: 'meadow', dir: seg.dir, x: toPos(emergeLandX(seg, env), env), from: 'gather', first: seg.first };
    case 'settle':
    case 'idle':
      return { kind: 'meadow', dir: seg.dir, x: seg.x, from: 'sit' };
    default:
      // In the burrow: straight to the peek-out.
      return { kind: 'burrow', dir: seg.dir || 1, wait: 0 };
  }
}

function findSeg(pl, t) {
  const segs = pl.segs;
  let i = pl.idx;
  if (i >= segs.length || segs[i].t0 > t) i = 0;
  while (i < segs.length - 1 && segs[i].t1 <= t) i++;
  pl.idx = i;
  return segs[i];
}

// Cut the timeline now and start hopping from where the rabbit is (debug):
// an idle eases back into the sit first, a settle is allowed to finish.
export function interruptWithHop(pl, t, env) {
  ensurePlan(pl, t, env);
  const seg = findSeg(pl, t);
  if (seg.type !== 'idle' && seg.type !== 'settle' && seg.type !== 'away' && seg.type !== 'hidden') return false;
  pl.segs.length = pl.idx + 1;
  if (seg.type === 'idle') {
    seg.cut = true;
    seg.dur = Math.min(seg.dur, t - seg.t0 + 220);
  } else if (seg.type !== 'settle') {
    seg.dur = t - seg.t0;
  }
  seg.t1 = seg.t0 + seg.dur;
  pl.end = seg.t1;
  pl.next = seg.type === 'hidden' ? { kind: 'burrow', dir: seg.dir || 1 } : nextAfter(seg, env);
  return true;
}

// The hero width changed. Hop timings follow from hop lengths, so whatever
// was planned after the current action (laid out for the old width) is
// dropped and planned again, lazily, from where that action ends. The
// current action finishes in proportion: a hop keeps its length in body
// sizes, and the jump out of the burrow and the dive into it are measured
// from the burrow anyway. A settle runs on into its stop, and the wait in the
// burrow into the peek-out, as neither depends on the width.
export function replan(pl, t, env) {
  if (!pl.segs.length || pl.end <= t) return false;
  const seg = findSeg(pl, t);
  let last = pl.idx;
  const after = pl.segs[last + 1];
  if (after && ((seg.type === 'settle' && after.type === 'idle') || (seg.type === 'hidden' && after.type === 'emerge'))) {
    last++;
  }
  if (seg.type === 'hop' && seg.bl) {
    seg.x1 = toPos(posX(seg.x0, env) + seg.dir * seg.bl * env.S, env);
  }
  const end = pl.segs[last];
  pl.segs.length = last + 1;
  pl.end = end.t1;
  pl.next = nextAfter(end, env);
  return true;
}

/* ---------------------------------------------------------------------- */
/* Evaluation                                                             */
/* ---------------------------------------------------------------------- */

export function newFrame() {
  return {
    visible: false,
    x: 0,
    face: 1,
    rootY: 0,
    a: 0,
    pose: copyPose(newPose(), POSES.sit),
    clip: false,
    deep: false,
    ground: true,
    lip: 0,
    shadow: 1,
    nose: 0.6,
    chew: 0,
    blink: 0,
    paws: 0,
    crumbT: -1,
    crumbDir: 1,
    puffT: -1,
    puffX: 0
  };
}

// A hop, by phases: crouch (tc) -> push (tp) -> flight (ta) -> landing over
// the front paws (tl) -> gather (tg). Sets fr.a (distance travelled along the
// hop, rig units) and fr.rootY; contacts are pinned in the world while they
// touch the ground.
//
// The path of the body centre is planned first (Hermite segments whose
// velocities match at every phase boundary, a parabola in flight) and the rig
// origin is derived from it, so the pose blends cannot add a hitch at
// take-off or touch-down. The hind legs run on their own timing: they trail
// behind after take-off, fold forward under the belly while the body is
// high, and touch down a beat after the front paws.
function hopCore(seg, lt, D, fr, fromPose, launchPose) {
  const P = fr.pose;
  const y0 = seg.y0 || 0;
  const t1 = seg.tc;
  const t2 = t1 + seg.tp;
  const t3 = t2 + seg.ta;
  const t4 = t3 + seg.tl;
  const pushPose = t1 > 0 ? POSES.crouch : fromPose;
  // Body centre (x from the start origin, y from the ground).
  const xC = pushPose.cx;
  const xL = (seg.dp || DP) + launchPose.cx;
  const xE = D + POSES.gather.cx;
  const xR = Math.max(xE - DL, xL + 10);
  const v = (xR - xL) / seg.ta;
  const yC = pushPose.cy + y0;
  const yL = launchPose.cy + y0 * 0.2;
  const yR = POSES.reach.cy;
  // Vertical speed at take-off: what the planned apex needs, but never more
  // than the push can build up without dipping first.
  const m1y = clampTangent((yR - yL - 4 * seg.h) / seg.ta * seg.tp, yL - yC, 2.6);
  const vL = m1y / seg.tp;
  // The flight is a parabola leaving at that speed and coming down at yR.
  const fy = yR - yL - vL * seg.ta;
  const vy1 = (2 * (yR - yL) - vL * seg.ta) / seg.ta;
  let bx;
  let by;
  let wPush = 0;
  if (lt < t1) {
    blendInto(P, fromPose, POSES.crouch, ease.inOutSine(lt / t1));
    bx = P.cx;
    by = P.cy + y0;
  } else if (lt < t2) {
    const s = (lt - t1) / seg.tp;
    // Out of the burrow the body stays upright longer, so it rises clear of
    // the rim before it tips forward.
    wPush = y0 > 0 ? s * s * s : easeIn(s);
    blendInto(P, pushPose, launchPose, wPush);
    bx = hermite(s, xC, xL, 0, v * seg.tp);
    by = hermite(s, yC, yL, 0, m1y);
  } else if (lt < t3) {
    const s = (lt - t2) / seg.ta;
    if (s < 0.42) blendInto(P, launchPose, POSES.air, easeOut(s / 0.42));
    else blendInto(P, POSES.air, POSES.reach, easeIn((s - 0.42) / 0.58));
    // Hind legs: finish extending, then fold and swing forward while the
    // body is high.
    if (s < 0.22) blendKeys(P, HIND_KEYS, launchPose, POSES.air, easeOut(s / 0.22));
    else if (s < 0.55) blendKeys(P, HIND_KEYS, POSES.air, POSES.tuck, ease.inOutSine((s - 0.22) / 0.33));
    else blendKeys(P, HIND_KEYS, POSES.tuck, POSES.reach, ease.inOutSine(span(s, 0.55, 0.9)));
    bx = xL + (xR - xL) * s;
    by = yL + vL * seg.ta * s + fy * s * s;
  } else if (lt < t4) {
    const s = (lt - t3) / seg.tl;
    blendInto(P, POSES.reach, POSES.land, ease.outCubic(s));
    // The hind feet come down a beat after the front paws.
    blendKeys(P, HIND_KEYS, POSES.reach, POSES.land, ease.inOutSine(span(s, 0.12, 0.8)));
    bx = hermite(s, xR, xE, v * seg.tl, 0);
    // Carries the fall into the squash of the gather; the paws take the
    // impact, so the body never sinks into the ground.
    by = hermite(s, yR, POSES.gather.cy, clampTangent(vy1 * seg.tl, POSES.gather.cy - yR, 2.4), 0);
  } else {
    const s = Math.min((lt - t4) / seg.tg, 1);
    blendInto(P, POSES.land, POSES.gather, ease.inOutSine(s));
    bx = xE;
    by = POSES.gather.cy;
  }
  const a = bx - P.cx;
  const rootY = by - P.cy;

  if (lt < t3) {
    // Hind toes stay where they pushed from until they leave the ground.
    P.hNx = fromPose.hNx - a;
    P.hFx = fromPose.hFx - a;
  }
  if (lt < t2) {
    // So do the front paws, until they lift.
    P.fNx -= a * (1 - wPush);
    P.fFx -= a * (1 - wPush);
  } else {
    const frontLand = D + POSES.gather.fNx;
    P.fNx = frontLand - a;
    P.fFx = frontLand + 4 - a;
  }
  if (lt >= t3) {
    const hindLand = D + POSES.gather.hNx;
    P.hNx = hindLand - a;
    P.hFx = hindLand + 3 - a;
    // Follow-through: ears and tail keep moving after the touch-down.
    const u = lt - t3;
    const wob = Math.sin(u / 1000 * TAU * 3.4) * Math.exp(-u / 150) * (1 - span(lt, t4, t4 + seg.tg));
    P.eN += 9 * wob;
    P.eF += 7 * wob;
    P.tr -= 10 * wob;
  }
  fr.a = a;
  fr.rootY = rootY;
}

function evalHop(seg, lt, env, fr) {
  const k = env.S / 100;
  const X0 = posX(seg.x0, env);
  const X1 = posX(seg.x1, env);
  const D = Math.max((X1 - X0) * seg.dir / k, 40);
  hopCore(seg, lt, D, fr, seg.from === 'sit' ? POSES.sit : POSES.gather, POSES.launch);
  fr.x = X0 + seg.dir * fr.a * k;
  fr.face = seg.dir;
  fr.nose = 0.3;
}

function evalSettle(seg, lt, env, fr) {
  const P = fr.pose;
  const s = Math.min(lt / seg.dur, 1);
  blendInto(P, POSES.gather, POSES.sit, ease.inOutSine(s));
  // Front paws step forward one after the other.
  const sN = span(s, 0.06, 0.6);
  const sF = span(s, 0.3, 0.86);
  P.fNx = lerp(POSES.gather.fNx, POSES.sit.fNx, ease.inOutSine(sN));
  P.fNy = 4.5 * Math.sin(Math.PI * sN);
  P.fFx = lerp(POSES.gather.fFx, POSES.sit.fFx, ease.inOutSine(sF));
  P.fFy = 4 * Math.sin(Math.PI * sF);
  fr.x = posX(seg.x, env);
  fr.face = seg.dir;
}

function evalIdle(seg, lt, env, fr) {
  const P = fr.pose;
  copyPose(P, POSES.sit);
  let nose = 1;
  for (let i = 0; i < seg.events.length; i++) {
    const ev = seg.events[i];
    const u = lt - ev.t0;
    if (u <= 0 || u >= ev.dur) continue;
    if (ev.kind === 'nibble') {
      const w = ramp(u, ev.dur, 380, 360);
      blendInto(P, POSES.sit, POSES.nibble, w);
      // Chewing: quick little head bobs while the head is down.
      P.hr += 2.4 * Math.sin(u / 1000 * TAU * 4.6) * w;
      fr.chew = w;
      nose = 1.5;
    } else if (ev.kind === 'alert') {
      // Rises smoothly onto its hind legs and settles back down.
      const up = u < 430 ? ease.outCubic(u / 430) : 1;
      const down = u > ev.dur - 400 ? ease.inOutSine((ev.dur - u) / 400) : 1;
      blendInto(P, POSES.sit, POSES.alert, u < 430 ? up : down);
      const w = Math.min(up, down, 1);
      P.hr += 8 * Math.sin(u / 1000 * TAU / 1.7) * w;
      P.eF += 9 * Math.sin(u / 1000 * TAU / 1.1 + 1) * w;
      P.eN += 5 * Math.sin(u / 1000 * TAU / 1.4) * w;
      nose = 1.8;
    } else if (ev.kind === 'look') {
      const w = ramp(u, ev.dur, 300, 300);
      blendInto(P, POSES.sit, POSES.look, w);
    } else if (ev.kind === 'groom') {
      // Sits up, brings both paws to the muzzle and rubs them down over the
      // face a few times, the head bobbing along.
      const w = ramp(u, ev.dur, 420, 380);
      blendInto(P, POSES.sit, POSES.groom, w);
      // The elbows fold first and the shoulders follow, so the paws come up
      // past the chest instead of reaching forward.
      const wa = ease.inOutSine(sat(w * 1.7));
      const wb = w * w;
      const S0 = POSES.sit;
      const G = POSES.groom;
      P.fNa1 = lerp(S0.fNa1, G.fNa1, wb);
      P.fNa2 = lerp(S0.fNa2, G.fNa2, wa);
      P.fNa3 = lerp(S0.fNa3, G.fNa3, wa);
      P.fFa1 = lerp(S0.fFa1, G.fFa1, wb);
      P.fFa2 = lerp(S0.fFa2, G.fFa2, wa);
      P.fFa3 = lerp(S0.fFa3, G.fFa3, wa);
      const g = w * span(u, 320, 520) * (1 - span(u, ev.dur - 520, ev.dur - 320));
      const ph = u / 1000 * TAU * 2.4;
      const rub = Math.sin(ph);
      P.fNa2 += 18 * rub * g;
      P.fNa3 += 26 * rub * g;
      P.fNa1 -= 6 * rub * g;
      P.fFa2 += 16 * Math.sin(ph - 0.9) * g;
      P.fFa3 += 22 * Math.sin(ph - 0.9) * g;
      P.hr += 6 * Math.sin(ph + 0.6) * g;
      P.eN -= 6 * g;
      P.eF -= 4 * g;
      fr.paws = w > 0.5 ? 1 : 0;
      nose = 0.5;
    } else if (ev.kind === 'sniff') {
      const w = ramp(u, ev.dur, 300, 300);
      P.hr += 16 * w;
      P.cx += 2 * w;
      P.eN -= 6 * w;
      nose = 2.2;
    }
  }
  for (let i = 0; i < seg.twitches.length; i++) {
    const tw = seg.twitches[i];
    const v = flick((lt - tw.t) / 300) * tw.amp;
    if (tw.ear) P.eF += v;
    else P.eN += v;
  }
  // Breathing and a slow drift of the head keep it from ever looking frozen
  // (faded in and out so the idle joins the hops seamlessly).
  const live = span(lt, 0, 400) * (1 - span(lt, seg.dur - 400, seg.dur));
  const b = Math.sin(lt / 1000 * TAU * 1.1 + seg.phase) * live;
  P.st += 0.03 * b;
  P.cy -= 0.3 * b;
  P.hr += 1.8 * Math.sin(lt / 1000 * TAU * 0.31 + seg.phase) * live;
  P.eN += 1.5 * Math.sin(lt / 1000 * TAU * 0.23 + seg.phase * 2) * live;
  if (seg.cut) {
    // Cut short (debug triggerHop): settle back into the plain sit.
    blendInto(P, P, POSES.sit, span(lt, seg.dur - 220, seg.dur));
  }
  fr.x = posX(seg.x, env);
  fr.face = seg.dir;
  fr.nose = nose;
}

function evalEmerge(seg, lt, env, fr) {
  const k = env.S / 100;
  const Bx = burrowX(env);
  const P = fr.pose;
  const T = seg.T;
  fr.clip = true;
  fr.ground = false;
  fr.lip = 1;
  if (lt < T.dip) {
    copyPose(P, POSES.peek);
    let depth;
    if (lt < T.ears) {
      // Ear tips first, still flattened, perking up as they come out.
      const s = lt / T.ears;
      depth = lerp(150, 103, ease.outCubic(s));
      P.eN = lerp(-42, -2, ease.outCubic(span(lt, T.ears * 0.2, T.ears)));
      P.eF = lerp(-50, -16, ease.outCubic(span(lt, T.ears * 0.35, T.ears)));
    } else if (lt < T.hold) {
      // Listening: a single ear twitch.
      depth = 103 + 0.8 * Math.sin((lt - T.ears) / 1000 * TAU * 1.2);
      P.eN += flick(span(lt, T.ears + 40, T.ears + 340)) * 14;
      P.eF += 4 * Math.sin((lt - T.ears) / 1000 * TAU * 0.9);
    } else if (lt < T.head) {
      const s = span(lt, T.hold, T.head);
      depth = lerp(103, 70, ease.outCubic(s));
      P.hr -= 6 * bump(lt, T.hold, T.head);
    } else if (lt < T.look) {
      // Looking around: sniffs the air, an ear flick, perhaps ducks to turn
      // round, then a glance at the viewer.
      depth = 70 + 0.8 * Math.sin((lt - T.head) / 1000 * TAU * 1.1);
      P.hr -= 12 * bump(lt, T.head + 40, T.head + 520);
      P.eN += 6 * Math.sin((lt - T.head) / 1000 * TAU * 0.8);
      const after = T.turnAt > 0 ? T.turnAt + TURN_MS : T.head;
      P.eF += flick(span(lt, after + 360, after + 660)) * 16;
      // It keeps its profile: no turning the face toward the viewer.
      P.ht = 0;
      fr.nose = 1.8;
    } else if (lt < T.rise) {
      depth = lerp(70, 46, ease.inOutSine(span(lt, T.look, T.rise)));
    } else {
      // Anticipation before the jump.
      const s = ease.inOutSine(span(lt, T.rise, T.dip));
      depth = lerp(46, 53, s);
      blendInto(P, POSES.peek, PEEK_DIP, s);
    }
    let face = seg.dir;
    if (T.turnAt > 0) {
      // Turning round: a quick duck right into the hole (ears pricked up as
      // it drops), the turn happens out of sight, and it pops back up the
      // other way with a little overshoot and the ears flopping.
      const u = lt - T.turnAt;
      let dk = 0;
      if (u > 0 && u < 160) dk = easeIn(u / 160);
      else if (u >= 160 && u < 230) dk = 1;
      else if (u >= 230 && u < TURN_MS) dk = 1 - ease.outCubic((u - 230) / (TURN_MS - 230));
      if (u > 0 && u < TURN_MS) {
        const hide = Math.max(HIDE_DEPTH - depth, 0);
        depth += dk * hide;
        const drop = u < 230 ? bump(u, 0, 260) : 0;
        P.eN = lerp(P.eN, 8, drop);
        P.eF = lerp(P.eF, 2, drop);
        const pop = flick(span(u, 300, 640));
        P.eN -= 9 * pop;
        P.eF -= 6 * pop;
      }
      face = u < 195 ? seg.look : seg.dir;
    }
    fr.face = face;
    fr.rootY = depth;
    fr.x = Bx + face * PEEK_SHIFT * k;
    fr.shadow = 0;
    fr.nose = Math.max(fr.nose, 1.2);
    return;
  }
  const jl = lt - T.dip;
  const J = seg.jump;
  const X0 = Bx + seg.dir * PEEK_SHIFT * k;
  const X1 = emergeLandX(seg, env);
  const D = Math.max((X1 - X0) * seg.dir / k, 40);
  hopCore(J, jl, D, fr, PEEK_DIP, POSES.launchOut);
  fr.x = X0 + seg.dir * fr.a * k;
  fr.face = seg.dir;
  fr.clip = jl < J.tp + J.ta + J.tl;
  fr.lip = fr.clip ? 1 : 0;
  fr.ground = jl >= J.tp + J.ta * 0.5;
  fr.shadow = sat(1 - fr.rootY / 30);
  // Earth kicked out of the hole by the hind feet, and a soft puff where the
  // rabbit lands.
  fr.crumbT = jl - 0.4 * J.tp;
  fr.crumbDir = -seg.dir;
  fr.puffT = jl - J.tp - J.ta;
  fr.puffX = X0 + seg.dir * (D + POSES.gather.cx) * k;
}

function evalDive(seg, lt, env, fr) {
  const k = env.S / 100;
  const dir = seg.dir;
  const P = fr.pose;
  const X0 = burrowX(env) - dir * seg.gap * env.S;
  const t1 = seg.tc;
  const t2 = t1 + seg.tp;
  const t3 = t2 + seg.ta;
  const t4 = t3 + seg.tsink;
  // Body centre path, as in hopCore. The flight ends with the nose just
  // inside the mouth of the hole; then the body slides on down the tunnel.
  const xC = POSES.crouch.cx;
  const xL = seg.dp + POSES.launch.cx;
  const xH = diveReach(seg.gap);
  const v = (xH - xL) / seg.ta;
  const yC = POSES.crouch.cy;
  const yL = POSES.launch.cy;
  const yH = HOLE_LINE + 3 - NOSE_DIVE[1];
  const m1y = clampTangent((yH - yL - 4 * seg.h) / seg.ta * seg.tp, yL - yC, 2.6);
  const vL = m1y / seg.tp;
  const fy = yH - yL - vL * seg.ta;
  const vy1 = (2 * (yH - yL) - vL * seg.ta) / seg.ta;
  let bx;
  let by;
  let wPush = 0;
  if (lt < t1) {
    blendInto(P, POSES.sit, POSES.crouch, ease.inOutSine(lt / t1));
    bx = P.cx;
    by = P.cy;
  } else if (lt < t2) {
    const s = (lt - t1) / seg.tp;
    wPush = easeIn(s);
    blendInto(P, POSES.crouch, POSES.launch, wPush);
    bx = hermite(s, xC, xL, 0, v * seg.tp);
    by = hermite(s, yC, yL, 0, m1y);
  } else if (lt < t3) {
    const s = (lt - t2) / seg.ta;
    if (s < 0.4) blendInto(P, POSES.launch, POSES.air, easeOut(s / 0.4));
    else blendInto(P, POSES.air, POSES.dive, ease.inOutSine((s - 0.4) / 0.6));
    bx = xL + (xH - xL) * s;
    by = yL + vL * seg.ta * s + fy * s * s;
  } else {
    // Down the tunnel along the line of the body, at the flight's speed at
    // first, slowing; the hind feet kick as they go in and the tail is the
    // last thing to disappear, with a flick.
    const s = span(lt, t3, t4);
    copyPose(P, POSES.dive);
    const m0 = Math.min(Math.max((v * SLIDE[0] + vy1 * SLIDE[1]) * seg.tsink, 0), 2.4 * DIVE_DEPTH);
    const d = hermite(s, 0, DIVE_DEPTH, m0, 0);
    bx = xH + SLIDE[0] * d;
    by = yH + SLIDE[1] * d;
    const kick = bump(s, 0.3, 0.75);
    P.hNa2 -= 26 * kick;
    P.hNa3 -= 40 * kick;
    P.hFa2 -= 20 * bump(s, 0.36, 0.8);
    P.hFa3 -= 34 * bump(s, 0.36, 0.8);
    P.tr += 22 * Math.sin(s * TAU * 1.5) * (1 - s);
    P.eN -= 8 * s;
  }
  const a = bx - P.cx;
  if (lt < t3) {
    // Toes pinned to the take-off spot until the legs fold away.
    P.hNx = POSES.sit.hNx - a;
    P.hFx = POSES.sit.hFx - a;
  }
  if (lt < t2) {
    P.fNx -= a * (1 - wPush);
    P.fFx -= a * (1 - wPush);
  }
  fr.x = X0 + dir * a * k;
  fr.face = dir;
  fr.rootY = by - P.cy;
  fr.clip = true;
  // Once airborne nothing may show below the rim, even beside the hole.
  fr.deep = lt >= t2 + 60;
  fr.ground = lt < t2;
  fr.lip = 1;
  fr.shadow = sat(1 - (fr.rootY - 6) / 24);
  fr.nose = 0.4;
  fr.crumbT = lt - t3 - 0.42 * seg.tsink;
  fr.crumbDir = -dir;
  if (lt >= t4) fr.visible = false;
}

export function evaluate(pl, t, env, fr) {
  ensurePlan(pl, t, env);
  const seg = findSeg(pl, t);
  const lt = Math.max(0, t - seg.t0);
  fr.visible = true;
  fr.clip = false;
  fr.deep = false;
  fr.ground = true;
  fr.lip = 0;
  fr.rootY = 0;
  fr.shadow = 1;
  fr.nose = 0.6;
  fr.chew = 0;
  fr.blink = 0;
  fr.paws = 0;
  fr.crumbT = -1;
  fr.puffT = -1;
  fr.face = seg.dir || 1;
  fr.seg = seg.type;
  switch (seg.type) {
    case 'hop':
      evalHop(seg, lt, env, fr);
      break;
    case 'settle':
      evalSettle(seg, lt, env, fr);
      break;
    case 'idle':
      evalIdle(seg, lt, env, fr);
      break;
    case 'emerge':
      evalEmerge(seg, lt, env, fr);
      break;
    case 'dive':
      evalDive(seg, lt, env, fr);
      break;
    default:
      // In the burrow / away off-screen: nothing to draw (and nothing for
      // the front lip of the hole to cover).
      fr.visible = false;
  }
  fr.blink = Math.max(fr.blink, blinkAt(t, pl.seed));
  return fr;
}
